if (!customElements.get('shirt-customizer')) {
  customElements.define(
    'shirt-customizer',
    class ShirtCustomizer extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.productSection = this.closest('[data-testid="product-information"]') || document;
        this.galleryFront = this.productSection.querySelector('[data-shirt-gallery-front]');
        this.galleryBack = this.productSection.querySelector('[data-shirt-gallery-back]');
        this.galleryFrontBase = this.productSection.querySelector('[data-shirt-gallery-front-base]');
        this.galleryBackBase = this.productSection.querySelector('[data-shirt-gallery-back-base]');
        this.baseImages = this.readBaseImages();
        this.frontProperty = this.querySelector('[data-property-front]');
        this.frontColorProperty = this.querySelector('[data-property-front-color]');
        this.frontColorField = this.querySelector('[data-front-color-field]');
        this.frontMotifColor = null;
        this.backProperty = this.querySelector('[data-property-back]');
        this.namePositionProperty = this.querySelector('[data-property-name-position]');
        this.nameField = this.querySelector('[data-name-field]');
        this.nameInput = this.querySelector('[data-name-input]');
        this.namePreview = this.productSection.querySelector('[data-name-preview]');
        this.namePreviewWrap = this.namePreview?.parentElement;

        this.querySelectorAll('[data-choice-side]').forEach((choice) => {
          choice.addEventListener('click', () => this.selectChoice(choice));
        });

        this.querySelectorAll('[data-name-position]').forEach((choice) => {
          choice.addEventListener('click', () => this.selectNamePosition(choice));
        });

        this.querySelectorAll('[data-front-color]').forEach((choice) => {
          choice.addEventListener('click', () => this.selectFrontMotifColor(choice));
        });

        this.nameInput?.addEventListener('input', () => this.updateNameState());
        this.productSection.addEventListener('change', (event) => {
          if (event.target.closest?.('variant-picker')) this.syncBaseImages();
        });
        this.productSection.addEventListener('variant:update', () => {
          requestAnimationFrame(() => {
            this.syncBaseImages();
            requestAnimationFrame(() => this.updateNamePrice());
          });
        });

        if (this.namePreviewWrap && 'ResizeObserver' in window) {
          this.nameResizeObserver = new ResizeObserver(() => this.fitNamePreview());
          this.nameResizeObserver.observe(this.namePreviewWrap);
        }
        document.fonts?.ready?.then(() => this.fitNamePreview());

        this.syncBaseImages();
        this.updateFrontMotifColorState();
        this.syncGallery('front');
        this.syncGallery('back');
        this.updateNameAvailability();
        this.updateNameState();
      }

      selectedChoice(side) {
        return this.querySelector(`[data-choice-side="${side}"].is-selected`);
      }

      selectedNamePosition() {
        return this.querySelector('[data-name-position].is-selected');
      }

      readBaseImages() {
        const dataElement = this.productSection.querySelector('[data-shirt-base-images]');
        if (!dataElement?.textContent) return {};

        try {
          return JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error('T-shirtens billedoversigt kunne ikke læses.', error);
          return {};
        }
      }

      normalizeValue(value) {
        return String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '');
      }

      selectedVariantOptions() {
        const variantPicker = this.productSection.querySelector('variant-picker');
        if (!variantPicker) return [];

        return Array.from(variantPicker.querySelectorAll('.variant-option'))
          .map((optionGroup) => {
            const label = optionGroup.querySelector('legend, label');
            const name = label?.childNodes?.[0]?.textContent?.trim() || label?.textContent?.trim() || '';
            const value =
              optionGroup.querySelector('input:checked')?.value || optionGroup.querySelector('select')?.value || '';
            return { name: this.normalizeValue(name), value };
          })
          .filter((option) => option.value);
      }

      modelKey(value) {
        const normalized = this.normalizeValue(value);
        return ['dame', 'kvinde', 'women', 'woman', 'female'].includes(normalized) ? 'dame' : 'unisex';
      }

      colorKey(value) {
        const normalized = this.normalizeValue(value);
        const colors = {
          sort: 'sort',
          black: 'sort',
          bl: 'sort',
          navy: 'navy',
          na: 'navy',
          arcticblue: 'arctic-blue',
          ab: 'arctic-blue',
          white: 'white',
          hvid: 'white',
          wh: 'white',
          offwhite: 'offwhite',
          offhvid: 'offwhite',
          ow: 'offwhite',
          aqua: 'aqua',
          aq: 'aqua',
          army: 'army',
          armygreen: 'army',
          ar: 'army',
          navyroyal: 'navy-royal',
          nr: 'navy-royal',
          royal: 'royal',
          royalblue: 'royal',
          rb: 'royal',
          blackgrey: 'black-grey',
          blackgray: 'black-grey',
          sortgra: 'black-grey',
          bg: 'black-grey',
        };
        return colors[normalized] || null;
      }

      syncBaseImages() {
        const options = this.selectedVariantOptions();
        const modelOption = options.find((option) => option.name === 'model');
        const colorOption = options.find((option) => ['farve', 'color', 'colour'].includes(option.name));
        const modelValue = modelOption?.value || options.find((option) => this.modelKey(option.value) === 'dame')?.value;
        const colorValue = colorOption?.value || options.find((option) => this.colorKey(option.value))?.value;
        const model = this.modelKey(modelValue);
        const color = this.colorKey(colorValue) || 'sort';
        const images = this.baseImages[color] || this.baseImages.sort;
        this.currentMotifTone = ['white', 'offwhite', 'arctic-blue'].includes(color) ? 'black' : 'white';

        if (!images) return;

        const frontUrl = images.front?.[model] || images.front?.unisex;
        const backUrl = images.back;
        const modelLabel = model === 'dame' ? 'dame' : 'unisex';
        const colorLabel = colorOption?.value || colorValue || 'Sort';

        if (frontUrl && this.galleryFrontBase) {
          this.galleryFrontBase.src = frontUrl;
          this.galleryFrontBase.alt = `${colorLabel} ${modelLabel} T-shirt set forfra`;
        }
        if (backUrl && this.galleryBackBase) {
          this.galleryBackBase.src = backUrl;
          this.galleryBackBase.alt = `${colorLabel} ${modelLabel} T-shirt set bagfra`;
        }

        this.querySelectorAll('[data-shirt-thumbnail-front-base]').forEach((image) => {
          if (frontUrl) image.src = frontUrl;
        });
        this.querySelectorAll('[data-shirt-thumbnail-back-base]').forEach((image) => {
          if (backUrl) image.src = backUrl;
        });

        this.querySelectorAll('[data-thumbnail-image-white], [data-thumbnail-image-black]').forEach((image) => {
          const usesBlackFallback =
            this.currentMotifTone === 'black' &&
            !image.dataset.thumbnailImageBlack &&
            Boolean(image.dataset.thumbnailImageWhite);
          const toneUrl =
            this.currentMotifTone === 'black'
              ? image.dataset.thumbnailImageBlack || image.dataset.thumbnailImageWhite
              : image.dataset.thumbnailImageWhite || image.dataset.thumbnailImageBlack;
          if (toneUrl) image.src = toneUrl;
          image.style.filter = usesBlackFallback ? 'brightness(0)' : 'none';
        });

        const statementPreviewColor = this.frontMotifColor || this.currentMotifTone;
        this.querySelectorAll('[data-front-colorable="true"]').forEach((motifChoice) => {
          const preview = motifChoice.querySelector('[data-thumbnail-color-preview]');
          const imageUrl = this.frontColorImage(motifChoice, statementPreviewColor);
          if (preview && imageUrl) {
            preview.src = imageUrl;
            preview.style.filter = 'none';
          }
        });

        if (this.namePreview) this.namePreview.dataset.ink = this.currentMotifTone;
        this.syncGallery('front');
        this.syncGallery('back');
      }

      syncGallery(side) {
        const selected = this.selectedChoice(side);
        const galleryImage = side === 'front' ? this.galleryFront : this.galleryBack;
        if (!selected || !galleryImage) return;

        const isColorableStatement = side === 'front' && selected.dataset.frontColorable === 'true';
        const colorImageUrl = isColorableStatement ? this.frontColorImage(selected) : '';
        const toneImageUrl = isColorableStatement
          ? ''
          : this.currentMotifTone === 'black'
            ? selected.dataset.choiceImageBlack || selected.dataset.choiceImageWhite
            : selected.dataset.choiceImageWhite || selected.dataset.choiceImageBlack;
        const imageUrl = colorImageUrl || toneImageUrl || selected.dataset.choiceImage || '';
        const usesBlackFallback =
          !isColorableStatement &&
          this.currentMotifTone === 'black' &&
          !selected.dataset.choiceImageBlack &&
          Boolean(selected.dataset.choiceImageWhite || selected.dataset.choiceImage);
        const height = Number(selected.dataset.choiceHeight || 0);
        galleryImage.hidden = !imageUrl;
        galleryImage.dataset.design = selected.dataset.choiceDesign || 'none';
        if (imageUrl) {
          galleryImage.src = imageUrl;
          galleryImage.style.left = `${selected.dataset.choiceLeft || 50}%`;
          galleryImage.style.top = `${selected.dataset.choiceTop || 0}%`;
          galleryImage.style.width = `${selected.dataset.choiceWidth || 0}%`;
          galleryImage.style.height = height > 0 ? `${height}%` : 'auto';
          galleryImage.style.objectFit = height > 0 ? 'fill' : 'contain';
          galleryImage.style.filter = usesBlackFallback ? 'brightness(0)' : 'none';
        }
        galleryImage.alt = selected.dataset.choiceAlt;
      }

      frontColorImage(choice, color = this.frontMotifColor) {
        if (!choice || !color) return '';
        const datasetKey = `color${color.charAt(0).toUpperCase()}${color.slice(1)}`;
        return choice.dataset[datasetKey] || '';
      }

      selectFrontMotifColor(choice, syncGallery = true) {
        if (!choice) return;

        this.frontMotifColor = choice.dataset.frontColor;
        this.querySelectorAll('[data-front-color]').forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        if (this.frontColorProperty) {
          this.frontColorProperty.disabled = false;
          this.frontColorProperty.value = choice.dataset.colorLabel || '';
        }

        this.querySelectorAll('[data-front-colorable="true"]').forEach((motifChoice) => {
          const preview = motifChoice.querySelector('[data-thumbnail-color-preview]');
          const imageUrl = this.frontColorImage(motifChoice);
          if (preview && imageUrl) {
            preview.src = imageUrl;
            preview.style.filter = 'none';
          }
        });

        if (syncGallery) this.syncGallery('front');
      }

      updateFrontMotifColorState() {
        const selectedFront = this.selectedChoice('front');
        const isColorableStatement = selectedFront?.dataset.frontColorable === 'true';

        if (this.frontColorField) this.frontColorField.hidden = !isColorableStatement;

        if (!isColorableStatement) {
          if (this.frontColorProperty) {
            this.frontColorProperty.disabled = true;
            this.frontColorProperty.value = '';
          }
          return;
        }

        const defaultColor = this.currentMotifTone === 'black' ? 'black' : 'white';
        const color = this.frontMotifColor || defaultColor;
        const colorChoice = this.querySelector(`[data-front-color="${color}"]`);
        this.selectFrontMotifColor(colorChoice, false);
      }

      fitNamePreview() {
        if (!this.namePreview || !this.namePreviewWrap || this.namePreview.hidden) return;

        const wrapWidth = this.namePreviewWrap.clientWidth;
        if (!wrapWidth) return;

        const widthPercent = this.namePreview.dataset.namePosition === 'lower' ? 30 : 34;
        const targetWidth = (wrapWidth * widthPercent) / 100;
        const maximumFontSize = Math.min(24, Math.max(11, wrapWidth * 0.055));
        let smallest = 5.5;
        let largest = maximumFontSize;
        const text = (this.namePreview.textContent || '').toUpperCase();
        const styles = window.getComputedStyle(this.namePreview);
        const context = this.nameMeasureContext || document.createElement('canvas').getContext('2d');
        this.nameMeasureContext = context;

        this.namePreview.style.width = `${widthPercent}%`;

        for (let step = 0; step < 9; step += 1) {
          const candidate = (smallest + largest) / 2;
          context.font = `${styles.fontWeight} ${candidate}px ${styles.fontFamily}`;
          const measuredWidth = context.measureText(text).width + Math.max(0, text.length - 1) * candidate * 0.04;

          if (measuredWidth <= targetWidth - 6) {
            smallest = candidate;
          } else {
            largest = candidate;
          }
        }

        this.namePreview.style.fontSize = `${Math.floor(smallest * 10) / 10}px`;
      }

      updateNamePrice(nameEnabled = this.selectedNamePosition()?.dataset.namePosition !== 'none') {
        const feeCents = Number(this.dataset.nameFeeCents || 0);
        this.dataset.nameFeeActive = String(Boolean(nameEnabled));

        this.productSection.querySelectorAll('product-price[data-base-price-cents]').forEach((priceComponent) => {
          const basePriceCents = Number(priceComponent.dataset.basePriceCents || 0);
          const priceElement = priceComponent.querySelector('[ref="priceContainer"] .price:not(.price-range)');
          if (!priceElement || !basePriceCents) return;

          const displayedPriceCents = basePriceCents + (nameEnabled ? feeCents : 0);
          priceElement.textContent = `${new Intl.NumberFormat('da-DK', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(displayedPriceCents / 100)} kr`;
        });
      }

      selectChoice(choice) {
        const side = choice.dataset.choiceSide;

        this.querySelectorAll(`[data-choice-side="${side}"]`).forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        const propertyInput = side === 'front' ? this.frontProperty : this.backProperty;
        if (propertyInput) propertyInput.value = choice.dataset.choiceValue;

        if (side === 'front') this.updateFrontMotifColorState();
        this.syncGallery(side);
        if (side === 'back') this.updateNameAvailability();
        this.updateNameState();
      }

      updateNameAvailability() {
        const selectedBack = this.selectedChoice('back');
        const availablePositions = (selectedBack?.dataset.nameAvailability || 'upper').split(/\s+/);
        const noneChoice = this.querySelector('[data-name-position="none"]');

        this.querySelectorAll('[data-name-position]').forEach((item) => {
          const position = item.dataset.namePosition;
          const disabled = position !== 'none' && !availablePositions.includes(position);
          item.disabled = disabled;
          item.setAttribute('aria-disabled', String(disabled));
        });

        const selectedPosition = this.selectedNamePosition()?.dataset.namePosition || 'none';
        if (selectedPosition !== 'none' && !availablePositions.includes(selectedPosition) && noneChoice) {
          this.selectNamePosition(noneChoice);
        }
      }

      selectNamePosition(choice) {
        this.querySelectorAll('[data-name-position]').forEach((item) => {
          const selected = item === choice;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        if (this.namePositionProperty) {
          this.namePositionProperty.value = choice.dataset.choiceValue;
        }

        this.updateNameState();
      }

      updateNameState() {
        const selectedPosition = this.selectedNamePosition();
        const position = selectedPosition?.dataset.namePosition || 'none';
        const nameEnabled = position !== 'none';

        if (this.nameField) this.nameField.hidden = !nameEnabled;
        if (this.nameInput) {
          this.nameInput.disabled = !nameEnabled;
          this.nameInput.required = nameEnabled;
          this.nameInput.setAttribute('aria-required', String(nameEnabled));
        }

        if (this.namePreview) {
          const enteredName = this.nameInput?.value.trim();
          this.namePreview.textContent = enteredName || this.namePreview.dataset.placeholder;
          this.namePreview.dataset.namePosition = position;
          this.namePreview.hidden = !nameEnabled;
          if (nameEnabled) requestAnimationFrame(() => this.fitNamePreview());
        }

        this.updateNamePrice(nameEnabled);
      }
    }
  );
}
